import { Publication } from './../interfaces/publication';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CustomFieldsService } from './custom-fields.service';
import { HttpClient } from '@angular/common/http';
import { ReplaySubject } from 'rxjs';


@Injectable()
export class BbMacrosService {
	publication: Publication;
	playoutOptions: any[];

	public macroSubject: ReplaySubject<any[]>;

	constructor(
		private translate: TranslateService,
		private http: HttpClient,
		private customFieldsService: CustomFieldsService
	) {
		this.macroSubject = new ReplaySubject(1);
		this.macroSubject.next( this.getAllMacroOptions() );
		this.http.get('https://cdn.bluebillywig.com/apps/player/latest/playoutdef.json').subscribe((playoutDef: any) => {
			this.playoutOptions = this.optionsFromPlayoutDef(playoutDef);
			this.macroSubject.next( this.getAllMacroOptions() );
		});
	}

	optionsFromPlayoutDef(playoutDef: any[]) {
		const options = playoutDef.filter(opt => opt.visible !== 'false').map((option: any) => ({
			label: option.category + ' - ' + option.subcategory + ' - ' + option.label,
			value: option.name
		}));
		return options;
	}

	getAllMacroOptions() {

		let allOptions: any[] = [{
			label: this.translate.instant('LOCALSTORAGE_MACRO'),
			value: '{{localstorage.YOUR_KEY_HERE}}'
		},
		{
			label: this.translate.instant('QUERYSTRING_MACRO'),
			value: '{{querystring.YOUR_PARAMETER_HERE}}'
		}];
		// {{mediaclip.FIELDNAME}} options:
		const mcFields = [
			{
				label: this.translate.instant('ID'),
				type: 'string',
				value: 'id',
				description: ''
			},
			{
				label: this.translate.instant('TITLE'),
				type: 'string',
				value: 'title',
				description: ''
			},
			{
				label: this.translate.instant('DURATION'),
				type: 'int',
				value: 'length',
				description: 'FIELD_DESCRIPTION_LENGTH'
			},
			{
				label: this.translate.instant('VIEWS'),
				type: 'int',
				value: 'views',
				description: 'FIELD_DESCRIPTION_VIEWS'
			},
			{
				label: this.translate.instant('TAG'),
				type: 'multistring',
				value: 'cat',
				description: ''
			},
			{
				label: this.translate.instant('TRANSCODING_FINISHED'),
				type: 'string',
				value: 'transcodingFinished',
				description: 'FIELD_DESCRIPTION_TRANSCODING_FINISHED'
			},
			{
				label: this.translate.instant('USER_ID'),
				type: 'string',
				value: 'userid',
				description: 'FIELD_DESCRIPTION_USER_ID'
			},
			{
				label: this.translate.instant('DEEP_LINK'),
				type: 'string',
				value: 'deeplink',
				description: 'FIELD_DESCRIPTION_DEEP_LINK'
			},
			{
				label: this.translate.instant('GENERATED_DEEP_LINK'),
				type: 'string',
				value: 'gendeeplink',
				description: 'FIELD_DESCRIPTION_GENERATED_DEEP_LINK'
			},
			{
				label: this.translate.instant('DESCRIPTION'),
				type: 'string',
				value: 'description',
				description: ''
			},
			{
				label: this.translate.instant('STATUS'),
				type: 'string',
				value: 'status',
				description: 'FIELD_DESCRIPTION_STATUS'
			},
			{
				label: this.translate.instant('COPYRIGHT'),
				type: 'string',
				value: 'copyright',
				description: ''
			},
			{
				label: this.translate.instant('MEDIATYPE'),
				type: 'string',
				value: 'mediatype',
				description: 'FIELD_DESCRIPTION_MEDIATYPE'
			},
			{
				label: this.translate.instant('SOURCETYPE'),
				type: 'string',
				value: 'sourcetype',
				description: 'FIELD_DESCRIPTION_SOURCETYPE'
			},
			{
				label: this.translate.instant('AUTHOR'),
				type: 'string',
				value: 'author',
				description: ''
			},
			{
				label: this.translate.instant('ORIGINAL_FILENAME'),
				type: 'string',
				value: 'originalfilename',
				description: 'FIELD_DESCRIPTION_ORIGINAL_FILENAME'
			},
			{
				label: this.translate.instant('MODIFIED_DATE'),
				type: 'string',
				value: 'modifieddate',
				description: ''
			},
			{
				label: this.translate.instant('CREATED_DATE'),
				type: 'string',
				value: 'createddate',
				description: ''
			},
			{
				label: this.translate.instant('SOURCE_ID'),
				type: 'string',
				value: 'sourceid',
				description: 'FIELD_DESCRIPTION_SOURCE_ID'
			}];
		const customFields = this.customFieldsService.getCustomFields('mediaclip');

		const mediaclipOptions: any[] = [...mcFields, ...customFields];

		allOptions = allOptions.concat(mediaclipOptions.map(prop => ({
			label: this.translate.instant('MEDIACLIP') + ': ' + (prop ? prop.label : ''),
			value: `{{mediaclip.${prop ? (prop.value || prop.name) : ''}}}` // name for custom fields
		})));

		const playerProperties = [{
			label: 'CURRENT_TIME',
			value: 'currentTime'
		},
		{
			label: 'SUBTITLE_LANGUAGE',
			value: 'currentSubtitleLanguage'
		},
		{
			label: 'SUBTITLE_ISO_CODE',
			value: 'currentSubtitleIsoCode'
		},
		{
			label: 'AUDIO_TRACK_LANGUAGE',
			value: 'currentAudioTrackLanguage'
		},
		{
			label: 'MEDIACLIP_DURATION',
			value: 'duration'
		},
		{
			label: 'ENDED',
			value: 'ended'
		},
		{
			label: 'VOLUME',
			value: 'volume'
		},
		{
			label: 'VIDEO_WIDTH',
			value: 'videoWidth'
		},
		{
			label: 'VIDEO_HEIGHT',
			value: 'videoHeight'
		},
		{
			label: 'IS_PLAYING',
			value: 'isPlaying'
		},
		{
			label: 'IS_MUTED',
			value: 'isMuted'
		},
		{
			label: 'IS_FULLSCREEN',
			value: 'isFullscreen'
		},
		{
			label: 'IS_LIVE',
			value: 'isLive'
		},
		{
			label: 'ID',
			value: 'id'
		}];

		allOptions = allOptions.concat(playerProperties.map(prop => ({
			label: this.translate.instant('PLAYER') + ': ' + this.translate.instant(prop.label),
			value: `{{player.${prop.value}}}`
		})));

		const deviceOptions = [{
			label: 'IS_MOBILE',
			value: 'isMobileDevice',
		},
		{
			label: 'MEASURED_BANDWIDTH',
			value: 'measuredBandwidth',
		},
		{
			label: 'SUPPORTS_OBJECT_FIT',
			value: 'objectFit',
		},
		{
			label: 'SUPPORTS_WEBGL',
			value: 'hasWebGL',
		},
		{
			label: 'IN_IFRAME',
			value: 'inIframe',
		},
		{
			label: 'IS_UI_WEBVIEW',
			value: 'isUIWebView',
		},
		{
			label: 'IS_WINDOWS_PHONE',
			value: 'isWindowsPhone',
		},
		{
			label: 'IS_MOBILE_DEVICE',
			value: 'isMobileDevice',
		},
		{
			label: 'IS_IPAD',
			value: 'isiPad',
		},
		{
			label: 'IPAD_VERSION',
			value: 'iPadVersion',
		},
		{
			label: 'IPAD_VERSIONCHECK',
			value: 'iPadVersionCheck',
		},
		{
			label: 'IS_IPHONE',
			value: 'isiPhone',
		},
		{
			label: 'IOS_VERSION',
			value: 'iOSVersion',
		},
		{
			label: 'IS_IOS',
			value: 'isiOS',
		},
		{
			label: 'IS_ANDROID',
			value: 'isAndroid',
		},
		{
			label: 'IS_OLD_ANDROID_NATIVE_BROWSER',
			value: 'isAndroidNative',
		},
		{
			label: 'ANDROID_VERSION',
			value: 'androidVersion',
		},
		{
			label: 'CAN_CONTROL_VOLUME',
			value: 'canControlVolume',
		},
		{
			label: 'CAN_PLAY_INLINE',
			value: 'canPlayInline',
		},
		{
			label: 'DEVICE_PIXEL_RATIO',
			value: 'devicePixelRatio',
		},
		{
			label: 'RESOLUTION',
			value: 'deviceResolution',
		},
		{
			label: 'RESOLUTION_STRING',
			value: 'deviceResolutionString',
		},
		{
			label: 'ORIENTATION',
			value: 'deviceOrientation',
		},
		{
			label: 'IS_INTERNET_EXPLORER',
			value: 'isIE',
		},
		{
			label: 'INTERNET_EXPLORER_VERSION',
			value: 'ieVersion',
		},
		{
			label: 'IS_EDGE',
			value: 'isEdge',
		},
		{
			label: 'EDGE_VERSION',
			value: 'edgeVersion',
		},
		{
			label: 'SUPPORTS_HTML5_IMA',
			value: 'supportsHTML5IMA',
		},
		{
			label: 'IS_CHROME',
			value: 'isChrome',
		},
		{
			label: 'IS_FIREFOX',
			value: 'isFirefox',
		},
		{
			label: 'IS_WEBKIT',
			value: 'isWebkit',
		},
		{
			label: 'IS_GECKO',
			value: 'isGecko',
		},
		{
			label: 'IS_OPERA',
			value: 'isOpera',
		},
		{
			label: 'IS_SAFARI',
			value: 'isSafari',
		},
		{
			label: 'HAS_TOUCH',
			value: 'hasTouch',
		},
		{
			label: 'IS_TOUCH_DEVICE',
			value: 'isTouchDevice',
		},
		{
			label: 'CAN_AUTOPLAY_ADVANCED',
			value: 'canAutoPlayAdvanced',
		},
		{
			label: 'SUPPORTS_SVG',
			value: 'svg'
		}];
		deviceOptions.forEach(opt => {
			opt.value = 'device_' + opt.value;
		});

		// one exception..
		deviceOptions.unshift({
			label: 'BROWSER_LANGUAGE',
			value: 'browserLanguage'
		});
		deviceOptions.forEach(prop => {
			prop.label = this.translate.instant('DEVICE') + ': ' + this.translate.instant(prop.label);
			prop.value = `{{player.${prop.value}}}`;
		});

		allOptions = allOptions.concat(deviceOptions);

		const assetOptions: any[] = [{
			label: 'ASSET_ID',
			value: 'id'
		},
		{
			label: 'ASSET_BANDWIDTH',
			value: 'bandwidth'
		},
		{
			label: 'ASSET_MEDIATYPE',
			value: 'media-type'
		},
		{
			label: 'ASSET_WIDTH',
			value: 'width'
		},
		{
			label: 'ASSET_HEIGHT',
			value: 'height'
		}];
		assetOptions.forEach(prop => {
			prop.label = this.translate.instant('ASSET') + ': ' + this.translate.instant(prop.label);
			prop.value = `{{asset.${prop.value}}}`;
		});

		allOptions = allOptions.concat(assetOptions);

		const playoutOpts: any[] = this.playoutOptions ? this.playoutOptions.map((option: any) => ({
			label:  this.translate.instant('PLAYOUT') + ': ' + option.label,
			value: `{{playout.${ option.value }}}`
		})) : [];
		allOptions = allOptions.concat(playoutOpts);
		allOptions = allOptions.map((opt) => ({
			label: opt.label,
			value: opt.value,
			aliases: [opt.value]
		}));
		return allOptions;
	}
}
